<template>
  <v-col class="pa-0">
    <!-- title -->
    <v-row class="ma-0 py-2">
      <h2>{{ $translate('SUGGESTIONS') }}</h2>

      <!-- Button to order all suggested products -->
      <v-btn
        color="primary"
        class="primary-font-color text-capitalize ml-auto ml-md-10 mb-2"
        :disabled="!hasProducts"
        :loading="addAllBtnLoading"
        @click="onOrderAll"
      >
        <!-- <v-icon left>add</v-icon> -->
        {{ $translate('ORDER_ALL_PRODUCT_SUGGESTIONS') }}
      </v-btn>
    </v-row>

    <!-- sugggestions -->
    <ItemGroup :items="suggestions">
      <template v-slot:item="{ item }">
        <ProductCard
          v-if="item.ObjectType === 'Product'"
          :product="item"
          style="height: 100%;"
          @view="onViewProduct(item)"
        />

        <v-hover v-else v-slot="{ hover }">
          <v-card width="100%" height="100%" :elevation="hover ? 4 : 1" class="colorCard">
            <v-img :src="item.PictureUrl" :aspect-ratio="1.618 / 1" />
            <v-card-title class="text-break pt-1 px-3">
              <div class="text-truncate">{{ item.Name }}</div>
            </v-card-title>
            <v-card-subtitle
              class="tdf-text-truncate px-3 py-1"
              v-html="item.Description"
            />

            <v-row justify="center" class="ma-0 py-2">
              <v-btn
                rounded
                elevation="0"
                color="primary"
                @click="onViewEvent(item)"
              >
                {{ $translate('VIEW') }}
              </v-btn>
            </v-row>
          </v-card>
        </v-hover>
      </template>
    </ItemGroup>

    <!-- product dialog -->
    <ProductOrderDialog
      :showDialog="showProductDialog"
      :product="selectedProduct"
      @close="showProductDialog = false"
    />

    <!-- Dialog for the response of adding all suggested products to shopping cart -->
    <AddProductsToCartSuccessDialog
      :dialogActiveProp="showAddAllProductsResponseDialog"
      :failedProducts="failedProducts"
      @close="closeAddAllProductsResponseDialog"
    />
  </v-col>
</template>

<script>
import doRequest, { addLog } from '@/api/dssRequest';
import uiHelpers from '@/shared/ui_helpers';
import ShoppingCart from '@/objects/ShoppingCart';
import ProductOrderDialog from '@/components/event/eventDetails/productOrderComponents/ProductOrderDialog';
import AddProductsToCartSuccessDialog from '@/components/generalComponents/dialogs/AddProductsToCartSuccessDialog';
import ProductCard from '@/components/generalComponents/product/ProductCard';
import ItemGroup from '@/components/generalComponents/uiComponents/ItemGroup';

export default {
  name: 'EventSuggestions',
  mixins: [uiHelpers],
  components: {
    ProductOrderDialog,
    AddProductsToCartSuccessDialog,
    ProductCard,
    ItemGroup,
  },
  props: {
    eventDetails: {
      type: Object,
      default: () => {},
    },
    suggestions: {
      type: Array,
      default: () => [],
    },
    scrollToSection: {
      required: false,
      type: Boolean,
      default: () => false,
    },
  },
  computed: {
    userIsLoggedIn() {
      return this.$store.getters['auth/isValid'];
    },
    userId() {
      return this.$store.getters['auth/userName']
        ? this.$store.getters['auth/userName']
        : this.$store.getters['auth/tmpUserId'];
    },
    hasProducts() {
      let result = false;
      if (this.suggestions != null) {
        const prodSuggestion = this.suggestions.find(
          (suggestion) => suggestion.ObjectType === 'Product'
        );
        if (prodSuggestion != null) {
          result = true;
        }
      }

      return result;
    },
  },
  data() {
    return {
      showProductDialog: false,
      showSuggestions: false,
      scrollToSectionRefs: this.scrollToSection,
      selectedProduct: {},
      addAllBtnLoading: false,
      showAddAllProductsResponseDialog: false,
      failedProducts: [],
    };
  },
  mounted() {
    this.showSuggestions = true;
    this.initPrimaryColorWithOpacity
  },
  updated() {
    if (this.scrollToSectionRefs) {
      this.scrollToSectionRefs = false;
      // scroll to the right position on that page
      window.EventBus.fire('scrollToRightPosition', '#eventDetailsSuggestions');
    }
  },
  methods: {
    openProductDialog(suggestion) {
      // set the product in selectedProduct
      this.selectedProduct = suggestion;
      // set the dialog to show
      this.showProductDialog = true;
    },
    getProductsByCategory(id) {
      const payload = {
        ProductCategoryId: id,
      };

      doRequest(
        (response) => {
          const category = response.data;
          // Clearing current Details for event page first, then fill
          this.$store.commit('setCurrentDetailsForEventPage', {});
          this.$store.commit('setCurrentDetailsForEventPage', {
            Type: 'ProductCategory',
            Id: response.data.ProductCategory.Id,
            Description: response.data.ProductCategory.Description,
            Name: response.data.ProductCategory.Name,
            Picture: response.data.ProductCategory.PictureUrl,
            Video: '',
            PortraitVideo: '',
          });
          // route to eventDetails
          this.goToPage('ProductCategory', {
            event_id: category.ProductCategory.EventId,
            category_id: category.ProductCategory.Id,
          });
        },
        () => {},
        'getProductsByCategory',
        this.userId,
        payload,
        'GET'
      );
    },
    getProduct(id) {
      const payload = {
        ProductIds: [id],
        IncludeAllPartnersInPartnerGroup: true,
      };

      doRequest(
        (response) => {
          const product = response.data.Products[0];
          // always go to the first category of the product
          const firstElement = 0;
          // route to the details of the product
          this.goToPage('ProductWithCategory', {
            event_id: product.EventId,
            category_id: product.Categories[firstElement].Id,
            product_id: product.Id,
          });
        },
        () => {},
        'getProducts',
        this.userId,
        payload,
        'GET'
      );
    },
    onOrderAll() {
      this.addAllBtnLoading = true;

      ShoppingCart.orderSuggestedProducts(this.eventDetails.EventId)
        .then(this.onOrderAllResponse)
        .catch(this.onOrderAllError);
    },
    onOrderAllResponse(response) {
      this.addAllBtnLoading = false;
      ShoppingCart.onOrderSuggestedProductsResponse(response);
      this.failedProducts = response.FailedProducts;
      this.showAddAllProductsResponseDialog = true;
    },
    onOrderAllError() {
      this.addAllBtnLoading = false;
      window.EventBus.fire(
        'activateSnackBar',
        this.$translate('ORDER_ALL_PRODUCT_SUGGESTIONS_FAILED')
      );
    },
    closeAddAllProductsResponseDialog() {
      this.showAddAllProductsResponseDialog = false;
      this.failedProducts = [];
    },
    onViewEvent(suggestion) {
      if (suggestion.ObjectType === 'Event') {
        addLog(this.userId, suggestion.EventId, 'Event', 'EventDetails', -1);
        this.goToPage('EventDetails', {
          event_id: suggestion.EventId,
        });
      }
    },
    onViewProduct(suggestion) {
      if (suggestion.ObjectType === 'Product') {
        addLog(this.userId, suggestion.Id, 'Product', 'EventDetails', -1);
      }
    },
    initPrimaryColorWithOpacity() {
      const opacityHexValue = '26';
      const selector = document.querySelector(':root');
      selector.style.setProperty(
        '--primary-color-opacity1',
        `${this.$vuetify.theme.themes.light.primary}${opacityHexValue}`
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.tdf-text-truncate {
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 3px;
  height: 6.9rem;
}
.overflow {
  overflow-x: auto;
};
.colorCard {
  background-color: var(--primary-color-opacity1);

}
</style>
